import React, { useState, useEffect } from 'react';
import './ConsentBanner.css';
import { Link } from 'gatsby';

const ConsentBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, can't be disabled
    analytics: false,
    marketing: false,
    functionality: false
  });

  // Check if consent has been given
  useEffect(() => {
    const checkConsent = () => {
      try {
        const consent = localStorage.getItem('cookie-consent');
        const sessionConsent = sessionStorage.getItem('cookie-consent-session');
        
        if (!consent && !sessionConsent) {
          // Double check - sometimes localStorage isn't immediately available after redirect
          setTimeout(() => {
            const retryConsent = localStorage.getItem('cookie-consent');
            const retrySessionConsent = sessionStorage.getItem('cookie-consent-session');
            
            if (!retryConsent && !retrySessionConsent) {
              setShowBanner(true);
            } else {
              const savedPreferences = JSON.parse(retryConsent || retrySessionConsent);
              setPreferences(savedPreferences);
              setShowBanner(false);
            }
          }, 100);
        } else {
          const savedPreferences = JSON.parse(consent || sessionConsent);
          setPreferences(savedPreferences);
          setShowBanner(false);
          // Don't call updateGoogleConsent here as it's already handled in head script
        }
      } catch (error) {
        console.error('Error checking consent:', error);
        setShowBanner(true); // Show banner if there's an error parsing
      }
    };

    checkConsent();
  }, []);

  const updateGoogleConsent = (prefs) => {
    if (typeof window !== 'undefined' && window.gtag) {
      // Use 'update' to immediately change consent without waiting
      window.gtag('consent', 'update', {
        'analytics_storage': prefs.analytics ? 'granted' : 'denied',
        'ad_storage': prefs.marketing ? 'granted' : 'denied',
        'ad_user_data': prefs.marketing ? 'granted' : 'denied',
        'ad_personalization': prefs.marketing ? 'granted' : 'denied',
        'functionality_storage': prefs.functionality ? 'granted' : 'denied',
        'security_storage': 'granted'
      });

      // Track consent event immediately after update
      setTimeout(() => {
        window.gtag('event', 'consent_update', {
          analytics_storage: prefs.analytics ? 'granted' : 'denied',
          ad_storage: prefs.marketing ? 'granted' : 'denied',
          functionality_storage: prefs.functionality ? 'granted' : 'denied',
          security_storage: 'granted',
          timestamp: new Date().toISOString(),
          consent_source: 'banner_interaction'
        });
      }, 100); // Small delay to ensure consent is applied first
    }
  };

  const saveConsent = (prefs) => {
    const consentData = {
      ...prefs,
      timestamp: new Date().toISOString(),
      version: '2.0'
    };
    
    // Update Google consent IMMEDIATELY before storing
    updateGoogleConsent(prefs);
    
    // Store consent data in both localStorage and sessionStorage
    const consentString = JSON.stringify(consentData);
    localStorage.setItem('cookie-consent', consentString);
    sessionStorage.setItem('cookie-consent-session', consentString);
    
    // Update UI state
    setShowBanner(false);
    setShowPreferences(false);
    
    // Dispatch custom event for other parts of the app
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('consentUpdated', { 
        detail: { preferences: prefs, timestamp: consentData.timestamp } 
      }));
    }
  };

  const acceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      functionality: true
    };
    setPreferences(allAccepted);
    saveConsent(allAccepted);
  };

  const rejectAll = () => {
    const rejected = {
      necessary: true,
      analytics: false,
      marketing: false,
      functionality: false
    };
    setPreferences(rejected);
    saveConsent(rejected);
  };

  const savePreferences = () => {
    saveConsent(preferences);
  };

  const handlePreferenceChange = (category) => {
    if (category === 'necessary') return; // Can't disable necessary cookies
    
    setPreferences(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  if (!showBanner) return null;

  return (
    <>
      {!showPreferences ? (
        <div className='consent-banner'>
          <div className='consent-content'>
            <div className='consent-text'>
              <h3>Gestione dei Cookie</h3>
              <p>
                Utilizziamo i cookie per migliorare la tua esperienza sul nostro sito. 
                Alcuni cookie sono necessari per il funzionamento del sito, mentre altri 
                ci aiutano a comprendere come interagisci con i nostri contenuti e a 
                personalizzare la tua esperienza. Per maggiori informazioni, consulta la nostra{' '}
                <Link to='/privacy-policy' target='_blank'>Privacy Policy</Link>.
              </p>
            </div>
            <div className='consent-buttons'>
              <button 
                className='btn btn-secondary' 
                onClick={() => setShowPreferences(true)}
              >
                Gestisci Preferenze
              </button>
              <button 
                className='btn btn-outline' 
                onClick={rejectAll}
              >
                Rifiuta Tutti
              </button>
              <button 
                className='btn btn-primary' 
                onClick={acceptAll}
              >
                Accetta Tutti
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className='consent-preferences'>
          <div className='preferences-content'>
            <div className='preferences-header'>
              <h3>Preferenze Cookie</h3>
              <button 
                className='close-btn' 
                onClick={() => setShowPreferences(false)}
                aria-label='Chiudi'
              >
                ×
              </button>
            </div>
            
            <div className='preferences-body'>
              <div className='cookie-category'>
                <div className='category-header'>
                  <div className='category-info'>
                    <h4>Cookie Necessari</h4>
                    <p>Questi cookie sono essenziali per il funzionamento e la sicurezza del sito web, inclusa la prevenzione delle frodi e l'autenticazione.</p>
                  </div>
                  <label className='toggle-switch' htmlFor='necessary-toggle'>
                    <input 
                      id='necessary-toggle'
                      type='checkbox' 
                      checked={preferences.necessary}
                      disabled
                    />
                    <span className='slider disabled'></span>
                  </label>
                </div>
              </div>

              <div className='cookie-category'>
                <div className='category-header'>
                  <div className='category-info'>
                    <h4>Cookie Analitici</h4>
                    <p>Ci aiutano a capire come i visitatori interagiscono con il sito web raccogliendo e riportando informazioni in forma anonima.</p>
                  </div>
                  <label className='toggle-switch' htmlFor='analytics-toggle'>
                    <input 
                      id='analytics-toggle'
                      type='checkbox' 
                      checked={preferences.analytics}
                      onChange={() => handlePreferenceChange('analytics')}
                    />
                    <span className='slider'></span>
                  </label>
                </div>
              </div>

              <div className='cookie-category'>
                <div className='category-header'>
                  <div className='category-info'>
                    <h4>Cookie di Marketing</h4>
                    <p>Utilizzati per tracciare i visitatori sui siti web per mostrare annunci pertinenti e coinvolgenti.</p>
                  </div>
                  <label className='toggle-switch' htmlFor='marketing-toggle'>
                    <input 
                      id='marketing-toggle'
                      type='checkbox' 
                      checked={preferences.marketing}
                      onChange={() => handlePreferenceChange('marketing')}
                    />
                    <span className='slider'></span>
                  </label>
                </div>
              </div>

              <div className='cookie-category'>
                <div className='category-header'>
                  <div className='category-info'>
                    <h4>Cookie di Funzionalità</h4>
                    <p>Permettono al sito web di ricordare le scelte che fai e fornire funzionalità migliorate e più personali.</p>
                  </div>
                  <label className='toggle-switch' htmlFor='functionality-toggle'>
                    <input 
                      id='functionality-toggle'
                      type='checkbox' 
                      checked={preferences.functionality}
                      onChange={() => handlePreferenceChange('functionality')}
                    />
                    <span className='slider'></span>
                  </label>
                </div>
              </div>
            </div>

            <div className='preferences-footer'>
              <button 
                className='btn btn-outline' 
                onClick={() => setShowPreferences(false)}
              >
                Annulla
              </button>
              <button 
                className='btn btn-primary' 
                onClick={savePreferences}
              >
                Salva Preferenze
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ConsentBanner;
